import { get, writable } from 'svelte/store';

import { real_id } from 'src/helpers/interface_helpers';

type StatusUpdatePayload = { id: string; msg: Record<string, unknown> };
type SingleStatPayload = { key: string; value: unknown };

function create_devices_status() {
	const { subscribe, set, update } = writable<Record<string, Record<string, unknown>>>({});

	let pendingFullUpdates: Record<string, Record<string, unknown>> = {};
	let pendingStatUpdates: Record<string, Record<string, unknown>> = {};
	let flushScheduled = false;

	const applyPendingUpdates = (
		status_list: Record<string, Record<string, unknown>>,
	): Record<string, Record<string, unknown>> => {
		const hasFull = Object.keys(pendingFullUpdates).length > 0;
		const hasStats = Object.keys(pendingStatUpdates).length > 0;

		if (!hasFull && !hasStats) {
			return status_list;
		}

		let next = status_list;

		for (const id in pendingFullUpdates) {
			next = { ...next, [id]: pendingFullUpdates[id] };
		}

		for (const id in pendingStatUpdates) {
			next = {
				...next,
				[id]: { ...next[id], ...pendingStatUpdates[id] },
			};
		}

		pendingFullUpdates = {};
		pendingStatUpdates = {};

		return next;
	};

	const scheduleFlush = () => {
		if (flushScheduled) {
			return;
		}

		flushScheduled = true;
		queueMicrotask(() => {
			flushScheduled = false;
			update((status_list) => applyPendingUpdates(status_list));
		});
	};

	return {
		set,
		subscribe,
		update_device_status: (new_status: StatusUpdatePayload) =>
			update((status_list) => {
				return { ...status_list, [real_id(new_status.id)]: new_status.msg };
			}),
		queue_device_status_update: (new_status: StatusUpdatePayload) => {
			pendingFullUpdates[real_id(new_status.id)] = new_status.msg;
			scheduleFlush();
		},
		update_single_stat: (id: string, stat: SingleStatPayload) =>
			update((status_list) => {
				const resolvedId = real_id(id);
				return {
					...status_list,
					[resolvedId]: { ...status_list[resolvedId], [stat.key]: stat.value },
				};
			}),
		queue_single_stat: (id: string, stat: SingleStatPayload) => {
			const resolvedId = real_id(id);
			if (!pendingStatUpdates[resolvedId]) {
				pendingStatUpdates[resolvedId] = {};
			}
			pendingStatUpdates[resolvedId][stat.key] = stat.value;
			scheduleFlush();
		},
		flush_device_status_updates: () => {
			if (
				!flushScheduled &&
				Object.keys(pendingFullUpdates).length === 0 &&
				Object.keys(pendingStatUpdates).length === 0
			) {
				return;
			}
			flushScheduled = false;
			update((status_list) => applyPendingUpdates(status_list));
		},
		remove: (id: string) =>
			update((status_list) => {
				const resolvedId = real_id(id);
				delete status_list[resolvedId];
				delete pendingFullUpdates[resolvedId];
				delete pendingStatUpdates[resolvedId];
				return status_list;
			}),
		reset: () => {
			pendingFullUpdates = {};
			pendingStatUpdates = {};
			flushScheduled = false;
			set({});
		},
		getByRealId: (id: string | undefined = undefined): any | undefined => {
			const resolvedId = real_id(id);
			const current = get(devices_status)[resolvedId];
			const pendingFull = pendingFullUpdates[resolvedId];
			const pendingStats = pendingStatUpdates[resolvedId];

			if (pendingFull) {
				return pendingStats ? { ...pendingFull, ...pendingStats } : pendingFull;
			}

			if (pendingStats) {
				return current ? { ...current, ...pendingStats } : pendingStats;
			}

			return current;
		},
	};
}

export const devices_status = create_devices_status();

export const getAllDeviceStatus = () => {
	return get(devices_status);
};

function create_devices_settings() {
	const { subscribe, set, update } = writable({});

	return {
		set,
		subscribe,
		update_devices_settings: (id, new_settings) =>
			update((settings_list) => {
				return { ...settings_list, [id]: new_settings };
			}),
		remove: (id) =>
			update((settings_list) => {
				delete settings_list[id];
				return settings_list;
			}),
		reset: () => set({}),
		getByRealId: (id: string | undefined = undefined): any | undefined => {
			return get(devices_settings)[real_id(id)];
		},
	};
}

export const devices_settings = create_devices_settings();

export const discovered_statuses = writable({});
export const discovered_settings = writable({});
export const discovered_info = writable({});
